import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";

export default function TrackingResult({ getStatusIcon, getStatusText }) {
  const { orderId } = useParams();
  const [trackingData, setTrackingData] = React.useState(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    async function fetchData() {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/track/${orderId}`);
        if (res.ok) {
          const data = await res.json();
          setTrackingData(data);
        }
      } catch (err) {
        console.error("Error fetching tracking data:", err);
      }
    }
    if (orderId) fetchData();
  }, [orderId]);

  if (!trackingData) return null;

  const statuses = ["confirmed", "shipped", "delivered"];
  const currentStatusIndex = statuses.indexOf(trackingData.status);

  const isCompleted = (index) => index <= currentStatusIndex;

  return (
    <div className="min-h-screen px-6 py-32">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold mb-8">Order Tracking</h1>

        <div className="bg-gray-50 p-6 rounded-lg mb-8">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Order ID</p>
              <p className="font-bold text-lg">{trackingData.orderId}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <p className="font-medium capitalize">{getStatusText(trackingData.status)}</p>
            </div>
          </div>
        </div>

        {statuses.map((status, idx) => (
          <div key={status} className="relative mb-8 last:mb-0">
            <div className="flex items-center gap-4">
              <div
                className={`w-12 h-12 flex items-center justify-center rounded-full transition-all duration-300 ${
                  isCompleted(idx)
                    ? "bg-green-500 text-white"
                    : "bg-gray-200 text-gray-400"
                }`}
              >
                {isCompleted(idx) ? (
                  <Check className="w-6 h-6" />
                ) : (
                  getStatusIcon(status)
                )}
              </div>
              <div>
                <p
                  className={`font-medium ${
                    isCompleted(idx) ? "text-black" : "text-gray-400"
                  }`}
                >
                  {getStatusText(status)}
                </p>
                {isCompleted(idx) && idx === currentStatusIndex && (
                  <p className="text-sm text-green-600 font-medium">Current Status</p>
                )}
              </div>
            </div>

            {idx < statuses.length - 1 && (
              <div
                className={`absolute left-6 top-12 w-0.5 h-8 transition-all duration-300 ${
                  isCompleted(idx + 1) ? "bg-green-500" : "bg-gray-300"
                }`}
              />
            )}
          </div>
        ))}

        <button
          onClick={() => navigate("/")}
          className="w-full mt-8 border border-black py-3 rounded font-medium hover:bg-gray-50 transition"
        >
          Back to Home
        </button>
      </div>
    </div>
  );
}